/**
 * Cloud log shipper. Sits behind fileLogger — every console.warn/error is
 * queued here and flushed to the backend in batches so the owner can review
 * counter activity remotely without asking staff to email log files.
 *
 * Always-on by design (no user-facing toggle). Skips info-level by default
 * to keep volume manageable; flip CLOUD_LOG_VERBOSE_KEY in localStorage to
 * '1' from the dev console if you need info-level on a specific device.
 *
 * Design choices:
 *  - In-memory buffer, persisted to localStorage on `beforeunload` so logs
 *    survive a renderer crash or quick close.
 *  - Flushes every 15s OR when buffer hits BATCH_SIZE entries.
 *  - Hard skip when offline. Resumes on `online` event.
 *  - Auth token pulled from the same storage as the rest of the app — if no
 *    token is present (sign-in screen) we silently no-op.
 */

import api from '../utils/api';

const BATCH_SIZE        = 50;
const MAX_BUFFER        = 500;        // anything beyond this is dropped (oldest first)
const FLUSH_INTERVAL_MS = 15_000;
const STORAGE_KEY       = 'pos_cloud_log_queue';
const DEVICE_ID_KEY     = 'pos_cloud_log_device_id';
// Hidden lever: set localStorage 'pos_cloud_log_verbose' = '1' on a specific
// device to push info-level too. No UI — for support use during debugging.
const VERBOSE_KEY       = 'pos_cloud_log_verbose';
const LEVEL_RANK = { debug: 0, info: 1, warn: 2, error: 3 };

let buffer = [];
let flushTimer = null;
let flushing = false;
let deviceId = null;
let appVersion = '';

const readStorage = (key, fallback = null) => {
  try { return localStorage.getItem(key); } catch (_) { return fallback; }
};
const writeStorage = (key, value) => {
  try { localStorage.setItem(key, value); } catch (_) { /* quota */ }
};

const getOrCreateDeviceId = () => {
  if (deviceId) return deviceId;
  let id = readStorage(DEVICE_ID_KEY);
  if (!id) {
    // Lightweight unique id — crypto.randomUUID isn't on every Electron build.
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    writeStorage(DEVICE_ID_KEY, id);
  }
  deviceId = id;
  return id;
};

const isVerbose = () => readStorage(VERBOSE_KEY, '0') === '1';

const minLevelRank = () => (isVerbose() ? LEVEL_RANK.info : LEVEL_RANK.warn);

const isOnline = () => (typeof navigator !== 'undefined' ? navigator.onLine !== false : true);

const isAuthed = () => {
  // Don't ship before sign-in — the backend rejects unauthenticated requests
  // anyway, but skipping the network call keeps logs from piling up while a
  // counter is parked on the login screen.
  try {
    return !!(
      localStorage.getItem('token') ||
      sessionStorage.getItem('token') ||
      JSON.parse(localStorage.getItem('user') || '{}')?.token
    );
  } catch (_) {
    return false;
  }
};

const loadPersistedBuffer = () => {
  try {
    const raw = readStorage(STORAGE_KEY);
    if (!raw) return;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr) && arr.length) {
      buffer = arr.slice(-MAX_BUFFER);
    }
  } catch (_) { /* corrupt — drop */ }
};
const persistBuffer = () => writeStorage(STORAGE_KEY, JSON.stringify(buffer.slice(-MAX_BUFFER)));

const scheduleFlush = () => {
  if (flushTimer || !buffer.length) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush().catch(() => { /* swallow — next tick retries */ });
  }, FLUSH_INTERVAL_MS);
};

const flush = async () => {
  if (flushing) return;
  if (!buffer.length) return;
  if (!isOnline()) return;
  if (!isAuthed()) return;

  flushing = true;
  try {
    const batch = buffer.slice(0, BATCH_SIZE);
    const payload = { entries: batch };
    await api.post('/logs/batch', payload, { timeout: 10_000 });
    buffer = buffer.slice(batch.length);
    persistBuffer();
    if (buffer.length) {
      // Still more queued — flush again right away.
      flushing = false;
      flush().catch(() => {});
      return;
    }
  } catch (err) {
    // Network error / 5xx — leave the batch in the buffer for next attempt.
    // Don't recurse on the same error.
  } finally {
    flushing = false;
    scheduleFlush();
  }
};

const detectIsElectron = () =>
  typeof window !== 'undefined' && !!window.electronAPI?.isElectron;

const captureAppVersion = async () => {
  if (appVersion) return;
  try {
    if (window.electronAPI?.getVersion) {
      appVersion = String(await window.electronAPI.getVersion() || '');
    }
  } catch (_) { /* ignore */ }
};

export const installCloudLogger = () => {
  if (typeof window === 'undefined') return;
  loadPersistedBuffer();
  captureAppVersion();

  // Whenever connectivity returns or auth lands, kick off a flush.
  window.addEventListener('online', () => { flush().catch(() => {}); });
  window.addEventListener('beforeunload', persistBuffer);
  // Periodic safety flush even if scheduleFlush slips somehow.
  setInterval(() => flush().catch(() => {}), FLUSH_INTERVAL_MS);

  // One boot marker per launch: version, environment, and which local storage
  // backend is actually working. Restart forensics used to require inferring
  // boots from socket-connect patterns; the storage backend field also shows
  // fleet-wide which machines run SQLite vs the IndexedDB fallback vs NOTHING.
  // Preload-binding forensics — if either contextBridge exposure threw,
  // preload.js writes the error to window.<name>__error. Surface it once so
  // packaged-installer bugs (e.g. missing window.electron on Biryani House 1.0.20)
  // stop being invisible.
  const electronBindError    = typeof window !== 'undefined' && window.electron__error;
  const electronAPIBindError = typeof window !== 'undefined' && window.electronAPI__error;
  if (electronBindError || electronAPIBindError) {
    console.error('[preload] contextBridge exposure failed', {
      electron: electronBindError || null,
      electronAPI: electronAPIBindError || null,
    });
  }

  // Cloud-log blocked duplicate launches. The single-instance lock silently
  // focuses the existing window — without this line, "two POS windows on one
  // laptop" reports were undiagnosable (no trace of the second attempt).
  // NOTE: only fires for the SAME install; two different installed versions
  // still run side by side and show up in app_logs as two device_ids.
  if (window.electronAPI?.onSecondInstanceBlocked) {
    window.electronAPI.onSecondInstanceBlocked(() => {
      console.warn('[app] second instance launch blocked — staff tried to open another copy');
    });
  }

  // Full-file log uploader: ships the daily electron-log files (main process
  // + complete renderer console) to the device_logs table, then deletes the
  // local copies once the cloud confirms — cloud is the store, disk is a
  // transit buffer. No-op on the web POS.
  import('./deviceLogUploader')
    .then(({ initDeviceLogUploader }) => initDeviceLogUploader())
    .catch(() => { /* non-fatal */ });

  // IPC surface audit: 1.0.43/1.0.44 installs behave as if the packaged
  // preload predates the renderer (dbCleanup resolves undefined while SQLite
  // is healthy). Log which bridge methods actually exist so the next build's
  // first boot proves which preload shipped — one line, warn level, cloud-fed.
  if (typeof window !== 'undefined' && window.electron?.isElectron) {
    console.warn('[app] ipc surface', {
      dbCleanup: typeof window.electron.dbCleanup === 'function',
      mirrorUpsertMany: typeof window.electron.mirrorUpsertMany === 'function',
      mirrorGetActive: typeof window.electron.mirrorGetActive === 'function',
      toggleFullscreen: typeof window.electronAPI?.toggleFullscreen === 'function',
      onSecondInstanceBlocked: typeof window.electronAPI?.onSecondInstanceBlocked === 'function',
    });
  }

  import('./localDB')
    .then(({ default: localDB }) => localDB.healthCheck())
    .then((health) => {
      console.warn('[app] started', {
        isElectron: window.electron?.isElectron === true,
        electronAPI: window.electronAPI?.isElectron === true,
        preloadIntact: !electronBindError && !electronAPIBindError,
        storageBackend: health?.backend || 'unknown',
        storageHealthy: health?.healthy === true,
        online: navigator.onLine,
      });
    })
    .catch(() => {
      console.warn('[app] started', {
        isElectron: window.electron?.isElectron === true,
        electronAPI: window.electronAPI?.isElectron === true,
        preloadIntact: !electronBindError && !electronAPIBindError,
        storageBackend: 'unknown',
        online: navigator.onLine,
      });
    });
};

/**
 * Queue a single log entry. Called from fileLogger.js inside its wrapped
 * console.* functions, so every existing console.error / console.warn in the
 * app surface flows through here automatically.
 */
export const queueCloudLog = ({ level = 'info', message = '', tag = null, context = null } = {}) => {
  const rank = LEVEL_RANK[level] ?? LEVEL_RANK.info;
  if (rank < minLevelRank()) return;
  if (!message) return;

  try {
    buffer.push({
      level,
      tag: tag || null,
      message: String(message).slice(0, 4000),
      context,
      deviceId: getOrCreateDeviceId(),
      appVersion: appVersion || undefined,
      isElectron: detectIsElectron(),
      branchId: getActiveBranchIdFromRedux() || undefined,
      clientTime: new Date().toISOString(),
    });
    if (buffer.length > MAX_BUFFER) {
      buffer = buffer.slice(-MAX_BUFFER);
    }
    if (buffer.length >= BATCH_SIZE) {
      flush().catch(() => {});
    } else {
      scheduleFlush();
    }
  } catch (_) { /* never break the call */ }
};

// Read the active branch id straight out of the Redux store if available, so
// each log line carries the operating branch context without us threading it
// through fileLogger.
const getActiveBranchIdFromRedux = () => {
  try {
    const store = window.__POS_STORE__;
    if (!store?.getState) return null;
    const state = store.getState();
    const id = state?.branch?.selectedBranchId;
    if (id && typeof id === 'string') return id;
    if (id && typeof id === 'object') return id._id || id.id || null;
    return null;
  } catch (_) {
    return null;
  }
};
